import React from 'react';
import {Card, Divider, Modal} from 'antd-mobile';
import {useNavigate} from 'react-router-dom';
import {t} from "../../../../i18n";
import {ExclamationCircleOutline} from "antd-mobile-icons";
import {PickupAppointmentType} from "../../graphql/types/PickupTaskWithAppointmentsType";
import {buildAddress1AndAddress2} from "../../../../utils/addressUtils";
import {formatTimestampToDateTime, formatTimestampByLanguage} from "../../../../utils/dateTimeUtils";
import {usePickupTaskOperations} from '../../store/pickupStore';
import {useTranslation} from "react-i18next";
import {getUnitLabel} from "../../../../enums/PickupEnum";

interface PickingUpCardProps {
    data: PickupAppointmentType;
}

const PickingUpCard: React.FC<PickingUpCardProps> = ({data}) => {
    const navigate = useNavigate();
    const status = data.status === 'CANCELLED' || data.status === 'TRANSFER_OUT';
    const startPoint = data.status === 'start_point';
    const endPoint = data.status === 'end_point';
    const { i18n } = useTranslation();
    const {finishTaskAppointment} = usePickupTaskOperations();

    const handleCardClick = () => {
        const mainContainer = document.getElementById('pickup-main-container') as HTMLElement;
        if (mainContainer) {
            sessionStorage.setItem('pickupPageScrollTop', mainContainer.scrollTop.toString());
        }
        if (status) {
            const handler = Modal.show({
                header: (
                    <ExclamationCircleOutline
                        style={{
                            fontSize: 64,
                            color: 'var(--adm-color-warning)',
                        }}
                    />
                ),
                title: t('pages.pickUp.pickingUp.cancelledTitle'),
                content: <div className="text-center">{t('pages.pickUp.pickingUp.cancelledContext')}</div>,
                actions: [
                    {
                        key: 'confirm',
                        text: t('pages.pickUp.pickingUp.iKnow'),
                        primary: true,
                        onClick: async () => {
                            await finishTaskAppointment({
                                taskId: data.taskId,
                                appointmentId: data.id
                            });
                            handler.close();
                        },
                    },
                ],
            });
            return;
        }
        navigate(`/pickup/picking-up/detail/${data.id}/${data.status}`);
    };

    return (
        <Card
            className="my-2 rounded-lg"
            style={{
                backgroundColor: status ? '#e5e7eb' : '#ffffff'
            }}
            onClick={handleCardClick}
        >
            <div className="flex items-baseline">
                <div className="text-base font-medium break-all flex-1 min-w-0 mr-3">
                    {data.channelName}
                </div>

                {/* 标签容器 - 与文本基线对齐 */}
                <div className="flex-shrink-0">
                    {/* 是否新仓和新预约展示逻辑 
                    1.如果是isNewWarehouse为true，则展示新地址（绿色背景）
                    2.如果isNewWarehouse为false，新预约按照原来逻辑展示，标签背景颜色为蓝色
                    */}
                    {data.isNewWarehouse && !status && (
                        <span className="bg-green-500 text-white text-sm font-medium px-2 py-1 rounded whitespace-nowrap">
                             {t('pages.pickUp.pickingUp.newWarehouse')}
                        </span>
                    )}
                    {!data.isNewWarehouse && (data.isNew === true && !status) && (
                        <span className="bg-blue-500 text-white text-sm font-medium px-2 py-1 rounded whitespace-nowrap">
                            {t('pages.pickUp.pickingUp.newAppointment')}
                        </span>
                    )}
                    {status && (
                        <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
                            {t('pages.pickUp.pickingUp.cancelled')}
                        </span>
                    )}
                </div>
            </div>
            <Divider/>
            <div className="flex gap-6">
                <div className="flex flex-col w-14 items-center justify-center gap-0.5">
                    <div className="text-2xl font-bold">
                        {startPoint ? t('pages.pickUp.start') : endPoint ? t('pages.pickUp.end') : data.sequence}
                    </div>
                </div>
                {(startPoint || endPoint) ? (
                    <div className="flex flex-col gap-1">
                        <h3 className="text-base font-semibold">{buildAddress1AndAddress2(data)}</h3>
                        <p>
                            <span>{t('pages.pickUp.pickingUp.appointmentTime')}：</span>
                            <span>{data.appointmentTime ? formatTimestampByLanguage(data.appointmentTime, i18n.language) : data.appointmentTime}</span>
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-1">
                        <h3 className="text-base font-semibold">{buildAddress1AndAddress2(data)}</h3>
                        <p>{t('pages.pickUp.pickingUp.appointmentPickUp')}：{data.quantity}{getUnitLabel(Number(data.unit), i18n.language)}</p>
                        <p>{t('pages.pickUp.pickingUp.appointmentPickUpDate')}：{data.appointmentTime ? formatTimestampByLanguage(data.appointmentTime, i18n.language) : data.appointmentTime}</p>
                        <p>
                            <span>{t('pages.pickUp.pickingUp.businessHours')}：</span>
                            <span style={{color: 'var(--adm-color-primary)'}}>{data.businessHours}</span>
                        </p>
                       <p hidden={!data.remark}>
                            <span>{t('pages.pickUp.pickingUp.remark')}：</span>
                            <span style={{color: 'blue'}}>{data.remark && data.remark.length > 20
                                ? `${data.remark.substring(0, 25)}...`
                                : data.remark}</span>
                        </p>

                    </div>
                )}


            </div>

        </Card>
    );
};

export default PickingUpCard;